import { Repeat, Sparkles, Star, Trash2 } from 'lucide-react';
import { useId, useMemo, useState } from 'react';
import type { Entry } from '../../shared/api';
import { loggedWithAI } from '../../shared/sources';
import { MEAL_LABELS, MEALS, type Meal } from '../../shared/dates';
import { scaleMap } from '../../shared/nutrients';
import { ApiError } from '../api/client';
import {
  useAddEntries,
  useDeleteEntry,
  useFavorites,
  useSettings,
  useStartRecurring,
  useStopRecurring,
  useToggleFavorite,
  useUpdateEntry,
} from '../api/hooks';
import { kcal } from '../lib/format';
import { useUI } from '../lib/ui';
import { FatSecretAttribution } from './FatSecretAttribution';
import { GramsInput } from './GramsInput';
import { NutritionPanel } from './NutritionPanel';
import { Sheet } from './Sheet';

export function EntrySheet({ entry, onClose }: { entry: Entry; onClose: () => void }) {
  const { toast, today } = useUI();
  const [grams, setGrams] = useState(entry.grams);
  const [meal, setMeal] = useState<Meal>(entry.meal);
  // The daily food this entry is that day's copy of. Kept here so the sheet updates in place.
  const [dailyId, setDailyId] = useState(entry.recurringId);
  const dailyHintId = useId();
  const update = useUpdateEntry();
  const remove = useDeleteEntry();
  const restore = useAddEntries();
  const startDaily = useStartRecurring();
  const stopDaily = useStopRecurring();
  const favorite = useToggleFavorite();
  const favorites = useFavorites();
  const settings = useSettings();
  const defs = useMemo(() => new Map((settings.data?.nutrients ?? []).map((n) => [n.key, n])), [settings.data]);
  const tracked = settings.data?.tracking.filter((t) => t.tracked).map((t) => t.key) ?? ['kcal', 'protein', 'carbs', 'fat'];
  const isFav = Boolean(entry.foodRef && favorites.data?.some((f) => f.ref === entry.foodRef));
  const n = scaleMap(entry.per100, grams);
  const dirty = grams !== entry.grams || meal !== entry.meal;

  // The sheet closes immediately. Callbacks passed to mutate() only fire while
  // the component is mounted, so these use the promise from mutateAsync().
  const showError = (e: unknown) => toast(e instanceof Error ? e.message : 'Something went wrong', { tone: 'error' });

  const save = () => {
    update.mutateAsync({ id: entry.id, date: entry.date, patch: { grams, meal } }).catch(showError);
    onClose();
  };

  const del = () => {
    const snapshot = {
      date: entry.date,
      meal: entry.meal,
      foodRef: entry.foodRef,
      name: entry.name,
      brand: entry.brand,
      grams: entry.grams,
      per100: entry.per100,
      servings: entry.servings,
      source: entry.source,
      confidence: entry.confidence,
      // Undo brings a daily copy back as that day's copy again.
      recurringId: dailyId,
    };
    remove
      .mutateAsync({ id: entry.id, date: entry.date })
      .then(() =>
        // A daily food keeps going: deleting one day's copy only skips that day.
        toast(dailyId ? `Skipped ${entry.name} for this day` : `Removed ${entry.name}`, {
          action: { label: 'Undo', onClick: () => restore.mutateAsync([snapshot]).catch(showError) },
        }),
      )
      .catch(showError);
    onClose();
  };

  const repeatDaily = () => {
    startDaily
      .mutateAsync(entry.id)
      .then((food) => {
        setDailyId(food.id);
        toast(`${entry.name} will be added every day`);
      })
      .catch(showError);
  };

  const stopRepeating = () => {
    if (!dailyId) return;
    const stopped = () => {
      setDailyId(null);
      toast(`Stopped repeating ${entry.name}`);
    };
    stopDaily
      .mutateAsync(dailyId)
      .then(stopped)
      // Already stopped (say on another device) is the same outcome.
      .catch((e: unknown) => (e instanceof ApiError && e.status === 404 ? stopped() : showError(e)));
  };

  return (
    <Sheet
      title={entry.name}
      onClose={onClose}
      headerExtra={
        <button
          className="btn btn--quiet btn--icon btn--sm"
          aria-label={isFav ? 'Remove from favorites' : 'Add to favorites'}
          aria-pressed={isFav}
          onClick={() =>
            favorite.mutate(
              isFav ? { ref: entry.foodRef ?? undefined, on: false } : { entryId: entry.id, on: true },
              { onSuccess: () => toast(isFav ? 'Removed from favorites' : 'Saved to favorites') },
            )
          }
        >
          <Star size={19} fill={isFav ? 'currentColor' : 'none'} />
        </button>
      }
      footer={
        <div className="row">
          <button className="btn btn--danger btn--icon" onClick={del} aria-label="Delete entry">
            <Trash2 size={18} />
          </button>
          <button className="btn btn--primary btn--block btn--lg" disabled={!dirty || grams <= 0} onClick={save}>
            Save · {kcal(n.kcal)} kcal
          </button>
        </div>
      }
    >
      <div className="stack" style={{ gap: 18 }}>
        <div className="row wrap small muted">
          {entry.brand && <span>{entry.brand}</span>}
          {loggedWithAI(entry) && (
            <span className="badge badge--ai">
              <Sparkles size={10} /> Logged with AI
            </span>
          )}
          {!entry.foodRef && !loggedWithAI(entry) && <span className="badge">Your entry</span>}
        </div>
        <GramsInput value={grams} onChange={setGrams} servings={entry.servings} kcalPer100={entry.per100.kcal} />
        <div className="seg seg--block" role="radiogroup" aria-label="Meal">
          {MEALS.map((m) => (
            <button key={m} role="radio" aria-checked={meal === m} className={`seg__btn ${meal === m ? 'is-active' : ''}`} onClick={() => setMeal(m)}>
              {MEAL_LABELS[m]}
            </button>
          ))}
        </div>
        <div className="card card--soft row wrap" style={{ padding: 12, gap: 12 }}>
          <Repeat size={18} aria-hidden="true" style={{ flex: 'none', color: 'var(--muted)' }} />
          <div className="grow" style={{ flexBasis: 160 }}>
            {dailyId ? (
              <>
                <div style={{ fontWeight: 600 }}>Added every day</div>
                <div id={dailyHintId} className="tiny muted">
                  Changes here are for this day only.
                </div>
              </>
            ) : (
              <div id={dailyHintId} className="small muted">
                {dirty
                  ? 'Save your changes first.'
                  : `Adds ${Math.round(entry.grams)} g to ${MEAL_LABELS[entry.meal].toLowerCase()} every day, from ${entry.date < today ? 'today' : 'tomorrow'}.`}
              </div>
            )}
          </div>
          {dailyId ? (
            <button className="btn btn--ghost btn--sm" disabled={stopDaily.isPending} onClick={stopRepeating}>
              Stop repeating
            </button>
          ) : (
            <button className="btn btn--ghost btn--sm" aria-describedby={dailyHintId} disabled={dirty || startDaily.isPending} onClick={repeatDaily}>
              Repeat every day
            </button>
          )}
        </div>
        <div className="card card--pad card--soft">
          <div className="eyebrow" style={{ marginBottom: 6 }}>
            Nutrition for {Math.round(grams)} g
          </div>
          <NutritionPanel per100={entry.per100} grams={grams} defs={defs} tracked={tracked} />
        </div>
        {entry.foodRef?.startsWith('fs:') && <FatSecretAttribution />}
      </div>
    </Sheet>
  );
}
